import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { breakpoints, getFluidFontSize, palette } from '../../../style/theme'
import RCIcon from '../../UI/RCIcon'
import { PDPOverlayWrapper } from '../shared'

// Whitelist of the visual suitability conditions we expose, in display order.
export const VISUAL_SUITABILITY_CONDITIONS = [
  'myopia',
  'hyperopia',
  'astigmatism',
  'presbyopia',
] as const

export type VisualSuitabilityCondition = typeof VISUAL_SUITABILITY_CONDITIONS[number]

export type VisualSuitabilityRow = {
  size: string
  [condition: string]: string
}

export type VisualSuitabilityData = VisualSuitabilityRow[]

type Props = {
  close: () => void
  data: VisualSuitabilityData
}

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const Wrapper = styled(PDPOverlayWrapper)`
  display: flex;
  flex-direction: column;
`

Wrapper.displayName = 'VisualSuitability'

const CloseBtn = styled.button`
  position: absolute;
  right: 1vmin;
  top: 1vmin;
  padding: 10px 15px;
  border-radius: 10px;
  box-shadow: 0 1px 10px 0 rgba(0, 0, 0, 0.05);
  display: flex;
  width: auto;
  justify-content: space-between;
  align-items: center;
  border: none;
  background-color: ${palette.white};
  font-size: ${getFluidFontSize('14px')};

  .rc-icon {
    margin-right: 0.5rem;
  }

  @media screen and (max-width: ${breakpoints.L}) {
    right: 1vw;
    padding: 0.7vh 0.4vw;
  }
`

const Title = styled.h2`
  text-align: center;
  text-transform: uppercase;
  font-size: ${getFluidFontSize('16px')};
  color: ${palette.doveGray};
  letter-spacing: 0.06em;
  margin: 2.5em 0 1.25em;
  font-weight: ${({ theme }) => theme.typography.fontWeights.regular};
  line-height: 3;
`

const TableWrapper = styled.div`
  flex: 1;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${getFluidFontSize('12px')};
`

const Th = styled.th`
  text-align: left;
  padding: 1em 0.8em;
  border: 1px solid ${palette.mercury};
  background-color: ${palette.wildSand};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  color: ${palette.mineShaft};
`

const Td = styled.td`
  padding: 0.75em 0.8em;
  border: 1px solid ${palette.mercury};
  vertical-align: middle;
  color: ${palette.tundora};
`

const SizeTd = styled(Td)`
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
`

const VisualSuitability: React.FC<Props> = ({ close, data }) => {
  const { t } = useTranslation()

  const columns = VISUAL_SUITABILITY_CONDITIONS.filter(condition =>
    data.some(row => row[condition]),
  )

  return (
    <Wrapper data-testid="visual-suitability">
      <CloseBtn onClick={close} className="close-visual-suitability">
        <RCIcon type="close" />
        <span>{t('GenericWords.close')}</span>
      </CloseBtn>
      <Title>{t('ProductPage.visualSuitabilityModalTitle')}</Title>
      <TableWrapper>
        <Table>
          <thead>
            <tr>
              <Th>{t('ProductPage.size')}</Th>
              {columns.map(col => (
                <Th key={col}>{t(`ProductPage.${col}`)}</Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map(row => (
              <tr key={row.size}>
                <SizeTd>{row.size}</SizeTd>
                {columns.map(col => (
                  <Td key={col}>{row[col]}</Td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrapper>
    </Wrapper>
  )
}

export default VisualSuitability
